import { useMemo, useCallback } from "react";

/**
 * A React hook that provides encryption and decryption functions using Web Crypto API
 * @param {Object} options - Configuration options
 * @param {string} options.password - The password for encryption/decryption
 * @param {string} options.salt - The salt for key derivation
 * @returns {Object} Encryption and decryption functions
 */

const decryptionPassword = process.env.REACT_APP_DECRYPTION_PASSWORD;
const decryptionSalt = process.env.REACT_APP_DECRYPTION_SALT;

export const useEncryption = ({
    password = decryptionPassword,
    salt = decryptionSalt,
} = {}) => {
    // Convert string to bytes
    const textToBytes = useCallback((text) => {
        return new TextEncoder().encode(text);
    }, []);

    // Convert bytes to string
    const bytesToText = useCallback((bytes) => {
        return new TextDecoder().decode(bytes);
    }, []);

    // Convert bytes to hex string
    const bytesToHex = useCallback((bytes) => {
        return Array.from(bytes)
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
    }, []);

    // Convert hex string to bytes
    const hexToBytes = useCallback((hex) => {
        const bytes = new Uint8Array(hex.length / 2);
        for (let i = 0; i < hex.length; i += 2) {
            bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
        }
        return bytes;
    }, []);

    // Derive key from password and salt
    const deriveKey = useCallback(async () => {
        const passwordBytes = textToBytes(password);
        const saltBytes = textToBytes(salt);

        // Import the password as raw key material
        const keyMaterial = await window.crypto.subtle.importKey(
            "raw",
            passwordBytes,
            { name: "PBKDF2" },
            false,
            ["deriveKey"],
        );

        // Derive a key using PBKDF2
        return window.crypto.subtle.deriveKey(
            {
                name: "PBKDF2",
                salt: saltBytes,
                iterations: 100000,
                hash: "SHA-256",
            },
            keyMaterial,
            { name: "AES-CBC", length: 256 },
            false,
            ["encrypt", "decrypt"],
        );
    }, [password, salt, textToBytes]);

    /**
     * Encrypts data using AES-CBC
     * @param {string} data - Data to encrypt
     * @returns {Promise<string>} Encrypted data in format "iv:ciphertext"
     */
    const encrypt = useCallback(
        async (data) => {
            const key = await deriveKey();
            const iv = window.crypto.getRandomValues(new Uint8Array(16));

            const encryptedData = await window.crypto.subtle.encrypt(
                {
                    name: "AES-CBC",
                    iv: iv,
                },
                key,
                textToBytes(data),
            );

            const encryptedArray = new Uint8Array(encryptedData);
            return `${bytesToHex(iv)}:${bytesToHex(encryptedArray)}`;
        },
        [deriveKey, textToBytes, bytesToHex],
    );

    /**
     * Decrypts data that was encrypted with the encrypt function
     * @param {string} encryptedData - Data in format "iv:ciphertext"
     * @returns {Promise<string>} Decrypted data
     */
    const decrypt = useCallback(
        async (encryptedData) => {
            const [ivHex, encryptedHex] = encryptedData.split(":");
            const iv = hexToBytes(ivHex);
            const encryptedBytes = hexToBytes(encryptedHex);

            const key = await deriveKey();

            const decryptedData = await window.crypto.subtle.decrypt(
                {
                    name: "AES-CBC",
                    iv: iv,
                },
                key,
                encryptedBytes,
            );

            return bytesToText(new Uint8Array(decryptedData));
        },
        [deriveKey, hexToBytes, bytesToText],
    );

    return {
        encrypt,
        decrypt,
        // Synchronous versions that return Promises internally
        encryptSync: (data) => encrypt(data),
        decryptSync: (data) => decrypt(data),
    };
};
